import {
  FeatureAreaKey,
  SelectedSource,
  SourceType,
  featureAreaKeys,
  schAloftArea,
  schSourceType,
} from '@aloft/toolkit-shared';

import React, { useEffect, useRef, useState } from 'react';
import { FieldPath, UseFormSetValue, useWatch } from 'react-hook-form';

import { dispatchAdjustmentsPageAlertTrigger } from '../../../../modules/alerts/dispatchAdjustmentsPageAlertTrigger.ts';
import { dispatchComparablesPageAlertTrigger } from '../../../../modules/alerts/dispatchComparablesPageAlertTrigger.ts';
import { useAlertsRef } from '../../../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectCommentaryV2 } from '../../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectDefaultFeatureAreas } from '../../../../redux/features/selectors/orderSelectors.ts';
import { selectFeatureGridActiveConflictedFields } from '../../../../redux/features/selectors/uiSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { PropertyGridForm, PropertyGridType } from '../../types.ts';
import { handleUpdatePropertyData } from '../../utils.ts';
import { pickBestFeatureArea } from '../../utils/applyEstimatedFeatureArea.ts';
import { PropertyGridFieldProps } from '../types.ts';
import { useFieldConflictDetection } from './useFieldConflictDetection.tsx';
import { usePropertyField } from './usePropertyField.tsx';
import { useSourceOptions } from './useSourceOptions.tsx';

const featureAreaFieldPattern = /improvements\.(\w+)\.area$/;

const featureAreaKeyFromFieldName = (fieldName: string): FeatureAreaKey | undefined => {
  const feature = featureAreaFieldPattern.exec(fieldName)?.[1];
  if (!feature || !(featureAreaKeys as readonly string[]).includes(feature)) return undefined;
  return feature as FeatureAreaKey;
};

const optionAreaSqft = (value: unknown): number | undefined => {
  const area = schAloftArea.safeParse(value);
  return area.success ? area.data.toSqFt().unitAmount : undefined;
};

type UseEditableFieldParams = Pick<
  PropertyGridFieldProps,
  'columnIndex' | 'control' | 'getValues' | 'required'
> &
  Required<Pick<PropertyGridFieldProps, 'isArv'>> & {
    field: Pick<
      PropertyGridFieldProps['field'],
      'fieldName' | 'toDisplayValue' | 'manualAdjustmentKey' | 'allowExplicitUnknown'
    >;
    gridType?: PropertyGridType;
    setValue: UseFormSetValue<PropertyGridForm>;
  };

export const useEditableField = ({
  columnIndex,
  field,
  isArv,
  control,
  getValues,
  setValue,
  required = false,
  gridType,
}: UseEditableFieldParams) => {
  const dispatch = useAppDispatch();
  const alertsRef = useAlertsRef();
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const allowExplicitUnknown = field.allowExplicitUnknown ?? !required;

  const {
    propertyId,
    isSubject,
    fieldName,
    propertyDataFieldName,
    selectedSourceFieldName,
    value,
    displayValue,
  } = usePropertyField({ columnIndex, field, isArv, control, getValues });

  const conflictedFields = useAppSelector(selectFeatureGridActiveConflictedFields);
  const defaultFeatureAreas = useAppSelector(selectDefaultFeatureAreas);
  const columns = getValues('columns');

  const selectedSourceFieldValue = useWatch({ control, name: selectedSourceFieldName }) as
    | SelectedSource
    | undefined;
  const selectedSourceValue = selectedSourceFieldValue?.source;
  const { conflictIsResolved } = selectedSourceFieldValue ?? {};
  const columnType = useWatch({ control, name: `columns.${columnIndex}.type` });

  const [showConflictButton, setShowConflictButton] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const customInput = useRef<HTMLInputElement>(
    null,
  ) as React.MutableRefObject<HTMLInputElement | null>;
  const userChoseSource = useRef(false);

  const options = useSourceOptions({
    columns,
    propertyId,
    fieldName,
    toDisplayValue: field.toDisplayValue,
    getValues,
    allowExplicitUnknown,
    defaultFeatureAreas,
  });

  const fieldHasConflict = Object.keys(conflictedFields).includes(propertyDataFieldName);

  useFieldConflictDetection({
    propertyDataFieldName,
    fieldName,
    propertyId,
    fieldHasConflict,
    conflictIsResolved,
    options,
  });

  const updatePropertyData = () => {
    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
    if (gridType === 'AdjustmentsGrid' && field.manualAdjustmentKey) {
      dispatchAdjustmentsPageAlertTrigger(dispatch, alertsRef, alertsCommentary);
    }
    if (gridType === 'FeatureGrid') {
      dispatchComparablesPageAlertTrigger(dispatch, alertsRef, alertsCommentary);
    }
  };

  const sourceAreasKey = options
    .map((option) => `${option.source}:${optionAreaSqft(option.rawValue) ?? ''}`)
    .join('|');

  useEffect(() => {
    if (userChoseSource.current) return;
    if (columnType !== 'property') return;
    const feature = featureAreaKeyFromFieldName(field.fieldName);
    if (!feature) return;

    const best = pickBestFeatureArea({
      feature,
      defaultAreas: defaultFeatureAreas,
      sourceAreas: options.map((option) => ({ source: option.source, area: option.rawValue })),
    });
    if (!best) return;

    const currentIsFallbackSource =
      !selectedSourceValue ||
      selectedSourceValue === schSourceType.enum.estimated ||
      selectedSourceValue === schSourceType.enum.none;
    const currentSqft = schAloftArea.safeParse(value).success
      ? schAloftArea.parse(value).toSqFt().unitAmount
      : undefined;
    const bestSqft = schAloftArea.parse(best.area).toSqFt().unitAmount;
    const currentIsHardcodedDefault =
      currentSqft != null && currentSqft !== bestSqft && best.source === schSourceType.enum.estimated;

    if (!currentIsFallbackSource && !currentIsHardcodedDefault) return;
    if (selectedSourceValue === best.source && currentSqft === bestSqft) return;

    setValue(propertyDataFieldName, best.area as never);
    setValue(`${selectedSourceFieldName}.source` as FieldPath<PropertyGridForm>, best.source);
    updatePropertyData();
  }, [
    columnType,
    defaultFeatureAreas,
    field.fieldName,
    sourceAreasKey,
    propertyDataFieldName,
    selectedSourceFieldName,
    selectedSourceValue,
    setValue,
    value,
  ]);

  const makeHandleOnClose = (onClose: () => void) => () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    updatePropertyData();
    onClose();
  };

  const resolveSource = (
    parsedNewValue?: string,
    newSource?: SourceType,
    displayParsedValue?: string,
  ): SourceType => {
    if (newSource === schSourceType.enum.unknown && allowExplicitUnknown) {
      return schSourceType.enum.unknown;
    }
    if (newSource) return newSource;
    if (selectedSourceValue && displayParsedValue === displayValue) return selectedSourceValue;
    const matchingOption = options.find(
      (option) =>
        option.value === displayParsedValue &&
        option.source !== schSourceType.enum.unknown &&
        option.source !== schSourceType.enum.estimated,
    );
    if (matchingOption) return matchingOption.source;
    if (parsedNewValue !== undefined) return 'custom';
    return 'none';
  };

  const commitSource = (resolvedSource: SourceType) => {
    setValue(`${selectedSourceFieldName}.source` as FieldPath<PropertyGridForm>, resolvedSource);
  };

  return {
    // from usePropertyField
    propertyId,
    isSubject,
    fieldName,
    propertyDataFieldName,
    selectedSourceFieldName,
    value,
    displayValue,
    // editable-field state
    conflictedFields,
    columns,
    selectedSourceFieldValue,
    selectedSourceValue,
    conflictIsResolved,
    showConflictButton,
    setShowConflictButton,
    inputFocused,
    setInputFocused,
    customInput,
    options,
    fieldHasConflict,
    updatePropertyData,
    makeHandleOnClose,
    resolveSource,
    commitSource,
    markUserChoseSource: () => {
      userChoseSource.current = true;
    },
    allowExplicitUnknown,
  };
};
